import React from 'react';
import { Investment } from '@tcs/client-portal-api/types/Investment';
import { useAccountCashTransactions } from '@tcs/client-portal-common/hooks/useAccountTransactions';
import TransactionsTable from '@tcs/client-portal-common/components/molecules/AssetActivityTable/';
import useAccount from '@tcs/client-portal-common/hooks/useAccount';
import useInterestRates from '@tcs/client-portal-common/hooks/useInterestRates';
import { addDays, format, parse, parseISO } from 'date-fns';
import { InterestRate } from '@tcs/client-portal-api/types/InterestRate';

interface Props {
  accountNumber: string;
  accountHolding: Investment;
  onBackButtonClick: () => void;
}

const getInterestRates = (
  accountType: string,
  interestRates: InterestRate[]
) => {
  if (interestRates) {
    const futureRate = interestRates.find(
      (x) => x.AccountType.toUpperCase() === accountType && !x.IsCurrent
    );
    if (futureRate) {
      const effectiveDate = parse(
        futureRate.EffectiveDate.split('T')[0],
        'yyyy-MM-dd',
        new Date()
      );
      const thirtyDaysFromNow = addDays(new Date(), 30);
      if (effectiveDate < thirtyDaysFromNow) {
        return {
          interestRate: futureRate.InterestRate,
          effectiveDate: format(effectiveDate, 'MM/dd/yyyy'),
        };
      }
    }

    const currentRate = interestRates.find(
      (x) => x.AccountType.toUpperCase() === accountType && x.IsCurrent
    );
    if (currentRate) {
      return {
        interestRate: currentRate.InterestRate,
        effectiveDate: format(
          parse(
            currentRate.EffectiveDate.split('T')[0],
            'yyyy-MM-dd',
            new Date()
          ),
          'MM/dd/yyyy'
        ),
      };
    }
  }
  return { interestRate: undefined, effectiveDate: undefined };
};

const CashTransactionsTable = ({
  accountNumber,
  accountHolding,
  onBackButtonClick,
}: Props) => {
  const { data: transactions, loading: transactionsLoading } =
    useAccountCashTransactions(accountNumber);
  const { data: account, loading: accountLoading } = useAccount(accountNumber);

  const { data: interestRates, loading: interestRatesLoading } =
    useInterestRates();
  const accountType =
    account.actualAccountType.toUpperCase() === 'PFC' ? 'PFC' : 'SDIRA';
  const { interestRate, effectiveDate } = getInterestRates(
    accountType,
    interestRates
  );

  return (
    <TransactionsTable
      accountNumber={accountNumber}
      accountHolding={accountHolding}
      transactions={transactions}
      showBalance
      showUnits={false}
      minimumCashBalance={account.minimumCashBalance}
      loading={transactionsLoading || accountLoading || interestRatesLoading}
      onBackButtonClick={onBackButtonClick}
      interestRate={interestRate}
      effectiveDate={effectiveDate}
    />
  );
};

export default CashTransactionsTable;
